import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, ArrowRight, MapPin, BatteryCharging, Zap, Leaf, VolumeX, IndianRupee, CheckCircle2, Camera, ChevronLeft, ChevronRight } from 'lucide-react';
import { trackEvent, EVENTS } from '@/lib/analytics';

const STATS = [
  { icon: BatteryCharging, value: '482 kWh', label: 'Battery Capacity' },
  { icon: Zap, value: '2', label: 'DG Sets Replaced' },
  { icon: VolumeX, value: '0 dB', label: 'Noise Level' },
  { icon: Leaf, value: '100%', label: 'Emission Reduction' },
];

const TIMELINE = [
  { title: 'Assessment', desc: 'Evaluated restaurant energy needs and DG usage patterns.' },
  { title: 'System Design', desc: '482 kWh BESS configured for peak shaving and full backup.' },
  { title: 'OPEX Model', desc: 'Structured as rental — zero upfront capital for the restaurant.' },
  { title: 'Deployment', desc: 'Rapid installation. Seamless transition from DG to BESS.' },
  { title: 'Operation', desc: 'Silent, zero-emission backup. Full remote monitoring.' },
];

const GALLERY = [
  { src: '/images/uru-hero.jpg', caption: 'Voltide BESS container arriving at Uru Brewpark' },
  { src: '/images/uru-gallery-1.png', caption: 'Voltide BESS unit — front panel with control systems' },
  { src: '/images/uru-gallery-2.png', caption: 'AC Block container — Voltide branding and access doors' },
  { src: '/images/uru-gallery-3.png', caption: 'Fully deployed Voltide BESS at Uru Brewpark — side view' },
  { src: '/images/uru-1.jpg', caption: 'Containerised BESS installed alongside existing infrastructure' },
];

export default function CaseStudyUruPage() {
  const [activePhoto, setActivePhoto] = useState(0);

  useEffect(() => {
    trackEvent(EVENTS.VIEW_CASE_STUDY, { study: 'uru-brewpark' });
  }, []);

  return (
    <div data-testid="case-study-uru-page">
      {/* Back Link */}
      <section className="pt-8">
        <div className="voltide-container">
          <Link to="/case-studies" className="inline-flex items-center gap-2 text-sm text-[#57534E] hover:text-[#0061DD] transition-colors" data-testid="back-to-case-studies">
            <ArrowLeft className="w-4 h-4" /> Back to Case Studies
          </Link>
        </div>
      </section>

      {/* Hero */}
      <section className="py-16 lg:py-24">
        <div className="voltide-container">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-up">
              <div className="flex flex-wrap gap-2 mb-6">
                <Badge className="rounded-full bg-blue-50 text-[#0061DD] border-blue-100">Restaurant</Badge>
                <Badge variant="secondary" className="rounded-full bg-stone-100 text-[#57534E]">
                  <MapPin className="w-3 h-3 mr-1" /> Bangalore, India
                </Badge>
              </div>
              <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-[#1A1A1A] leading-[1.1] mb-4">
                Uru Brewpark
              </h1>
              <p className="text-xl text-[#0061DD] font-semibold mb-6">
                India's first large-format restaurant to eliminate diesel generators entirely.
              </p>
              <p className="text-[#57534E] leading-relaxed mb-8">
                Through a Voltide-structured OPEX model, Uru Brewpark transitioned from 2 large diesel generators to a 482 kWh battery energy storage system without upfront capital expenditure. The result is silent, zero-emission backup power — proving that diesel-free commercial operations at scale are not only technically viable, but financially accessible today.
              </p>
              <Link to="/contact" onClick={() => trackEvent(EVENTS.START_RFQ, { source: 'case_study_uru' })}>
                <Button className="rounded-full bg-[#0061DD] hover:bg-[#004BB3] text-white px-8 py-6 text-lg font-semibold gap-2" data-testid="uru-get-quote-btn">
                  Get Similar Results <ArrowRight className="w-5 h-5" />
                </Button>
              </Link>
            </div>
            <div className="animate-fade-up stagger-2">
              <div className="rounded-2xl overflow-hidden shadow-lg">
                <img src="/images/uru-hero.jpg" alt="Voltide BESS delivery at Uru Brewpark, Bangalore" className="w-full h-full object-cover" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-white" data-testid="uru-stats">
        <div className="voltide-container">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {STATS.map((stat, i) => (
              <div key={stat.label} className="bento-card p-6 text-center" data-testid={`uru-stat-${i}`}>
                <stat.icon className="w-8 h-8 text-[#0061DD] mx-auto mb-3" />
                <p className="mono text-2xl sm:text-3xl font-bold text-[#1A1A1A]">{stat.value}</p>
                <p className="text-sm text-[#57534E] mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Installation Video */}
      <section className="py-16 lg:py-20" data-testid="uru-video">
        <div className="voltide-container">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#1A1A1A] text-center mb-3">Watch the Installation</h2>
            <p className="text-center text-[#57534E] mb-8">See how Voltide deployed 482 kWh of battery storage at Uru Brewpark</p>
            <div className="rounded-2xl overflow-hidden shadow-xl bg-[#0a0f1a]">
              <video
                controls
                preload="metadata"
                poster="/images/uru-hero.jpg"
                className="w-full aspect-video"
                data-testid="uru-video-player"
              >
                <source src="/images/uru-installation.mp4" type="video/mp4" />
                Your browser does not support the video element.
              </video>
            </div>
          </div>
        </div>
      </section>

      {/* Challenge & Solution */}
      <section className="py-16 lg:py-20" data-testid="uru-challenge-solution">
        <div className="voltide-container">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bento-card p-8">
              <h2 className="text-2xl font-bold text-[#1A1A1A] mb-4">The Challenge</h2>
              <ul className="space-y-3">
                {[
                  'Large-format restaurant requiring significant backup power',
                  'Two diesel generators causing noise and air pollution',
                  'High fuel costs and maintenance overhead',
                  'Urban location demanding silent, clean operation',
                ].map(item => (
                  <li key={item} className="flex items-start gap-3 text-sm text-[#57534E]">
                    <div className="w-2 h-2 rounded-full bg-red-400 mt-1.5 shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="bento-card p-8 bg-emerald-50/50 border-emerald-100">
              <h2 className="text-2xl font-bold text-[#1A1A1A] mb-4">The Solution</h2>
              <ul className="space-y-3">
                {[
                  '482 kWh Voltide BESS installed as DG replacement',
                  'OPEX rental model — zero upfront capital needed',
                  'Silent, zero-emission backup power delivery',
                  'Fully managed service with remote monitoring',
                ].map(item => (
                  <li key={item} className="flex items-start gap-3 text-sm text-[#1A1A1A]">
                    <CheckCircle2 className="w-5 h-5 text-emerald-500 mt-0.5 shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-16 lg:py-20 bg-white" data-testid="uru-timeline">
        <div className="voltide-container">
          <h2 className="text-2xl sm:text-3xl font-bold text-[#1A1A1A] text-center mb-12">Project Timeline</h2>
          <div className="max-w-2xl mx-auto space-y-0">
            {TIMELINE.map((step, i) => (
              <div key={step.title} className="flex gap-6" data-testid={`uru-timeline-${i}`}>
                <div className="flex flex-col items-center">
                  <div className="w-10 h-10 rounded-full bg-[#0061DD] text-white flex items-center justify-center text-sm font-bold shrink-0">
                    {i + 1}
                  </div>
                  {i < TIMELINE.length - 1 && <div className="w-0.5 h-full bg-blue-100 min-h-[3rem]" />}
                </div>
                <div className="pb-8">
                  <h3 className="text-lg font-bold text-[#1A1A1A]">{step.title}</h3>
                  <p className="text-sm text-[#57534E] mt-1">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Results */}
      <section className="py-16 lg:py-20" data-testid="uru-results">
        <div className="voltide-container">
          <h2 className="text-2xl sm:text-3xl font-bold text-[#1A1A1A] text-center mb-12">The Results</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Leaf, title: 'Zero Emissions', desc: '100% elimination of diesel exhaust from restaurant operations.' },
              { icon: VolumeX, title: 'Silent Power', desc: 'Complete noise elimination. Better dining experience for customers.' },
              { icon: IndianRupee, title: 'Cost Effective', desc: 'OPEX model means no upfront cost. Predictable monthly payments.' },
            ].map((r, i) => (
              <div key={r.title} className="bento-card p-6 text-center" data-testid={`uru-result-${i}`}>
                <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center mx-auto mb-4">
                  <r.icon className="w-6 h-6 text-[#0061DD]" />
                </div>
                <h3 className="text-lg font-bold text-[#1A1A1A] mb-2">{r.title}</h3>
                <p className="text-sm text-[#57534E]">{r.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Photo Gallery */}
      <section className="py-16 lg:py-20 bg-white" data-testid="uru-gallery">
        <div className="voltide-container">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#1A1A1A] flex items-center gap-2">
              <Camera className="w-6 h-6 text-[#0061DD]" /> Deployment Gallery
            </h2>
            <div className="flex gap-2">
              <button onClick={() => setActivePhoto(p => p === 0 ? GALLERY.length - 1 : p - 1)} className="w-10 h-10 rounded-full bg-stone-100 flex items-center justify-center hover:bg-stone-200 transition-colors" data-testid="gallery-prev">
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button onClick={() => setActivePhoto(p => (p + 1) % GALLERY.length)} className="w-10 h-10 rounded-full bg-stone-100 flex items-center justify-center hover:bg-stone-200 transition-colors" data-testid="gallery-next">
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Main Image */}
          <div className="rounded-2xl overflow-hidden mb-4 bg-stone-100">
            <img
              src={GALLERY[activePhoto].src}
              alt={GALLERY[activePhoto].caption}
              className="w-full aspect-[16/9] object-cover transition-opacity duration-300"
              data-testid="gallery-main-image"
            />
          </div>
          <p className="text-sm text-[#57534E] text-center mb-6">{GALLERY[activePhoto].caption}</p>

          {/* Thumbnails */}
          <div className="flex gap-3 justify-center overflow-x-auto pb-2" data-testid="gallery-thumbnails">
            {GALLERY.map((photo, i) => (
              <button
                key={i}
                onClick={() => setActivePhoto(i)}
                className={`shrink-0 w-24 h-16 rounded-lg overflow-hidden transition-all ${i === activePhoto ? 'ring-2 ring-[#0061DD] ring-offset-2 opacity-100' : 'opacity-50 hover:opacity-80'}`}
                data-testid={`gallery-thumb-${i}`}
              >
                <img src={photo.src} alt={photo.caption} className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-24 bg-[#0061DD]">
        <div className="voltide-container text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Want diesel-free operations for your business?
          </h2>
          <p className="text-lg text-blue-100 mb-8 max-w-xl mx-auto">
            Join Uru Brewpark in the clean energy revolution. Get a customized BESS rental quote today.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact">
              <Button className="rounded-full bg-white text-[#0061DD] hover:bg-blue-50 px-8 py-6 text-lg font-semibold gap-2" data-testid="uru-cta-quote-btn">
                Get a Quote <ArrowRight className="w-5 h-5" />
              </Button>
            </Link>
            <Link to="/calculator">
              <Button variant="outline" className="rounded-full border-white/40 text-white hover:bg-white/10 px-8 py-6 text-lg font-medium">
                Calculate Your Savings
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
